import React, { useState, useMemo } from 'react';
import { X, Loader2, ChevronDown, ChevronRight } from 'lucide-react';
import VariantImageManager from './VariantImageManager';

// Types
export interface ProductImage {
  id: number;
  variante_id: number;
  producto_variante_id: number;
  url: string;
  orden: number;
  principal: boolean;
}

export interface EditingVariant {
  id?: number;
  sku: string | null;
  nombre: string;
  descripcion: string | null;
  costo_promedio: string | number;
  precio_local: string | number;
  precio_web: string | number;
  precio_ml: string | number;
  atributos?: Record<string, string> | null;
  images?: ProductImage[];
}

export interface EditingProduct {
  id?: number;
  nombre: string;
  url_pagina_web: string | null;
  costo_promedio: string | number;
  precio_local: string | number;
  precio_web: string | number;
  precio_ml: string | number;
  categoria_id: number | null;
  categoria?: string;
  updated_at?: string;
  variants: EditingVariant[];
  applyPriceLocalToVariants?: boolean;
  applyPriceWebToVariants?: boolean;
  applyPriceMlToVariants?: boolean;
  applyDescriptionToVariants?: boolean;
  applyCostToVariants?: boolean;
}

export interface Category {
  id: number;
  nombre: string;
  path: string;
  parent_id?: number | null;
}

interface ProductEditFormProps {
  editingProduct: EditingProduct | null;
  setEditingProduct: React.Dispatch<React.SetStateAction<EditingProduct | null>>;
  categories: Category[];
  onSave: () => void;
  onCancel: () => void;
  saving: boolean;
  showDeleteVariantConfirmation: number | null;
  setShowDeleteVariantConfirmation: (id: number | null) => void;
  handleDeleteVariant: (id: number) => void;
  handleAddVariant: () => void;
  handleGenerateDescription: (type: 'product' | 'variant', variantId?: number) => void;
  generatingDescription: 'product' | 'variant' | null;
  uploadingImages: Record<number, boolean>;
  imageError: string | null;
  handleImageUpload: (variantId: number, files: File[]) => void | Promise<void>;
  handleDeleteImage: (variantId: number, imageId: number) => void;
  handleSetPrincipalImage: (variantId: number, imageId: number) => void;
  handleReorderImages: (variantId: number, imageId: number, direction: "up" | "down") => void;
  handleDeleteProduct?: () => void;
  showDeleteProductConfirmation?: boolean;
  setShowDeleteProductConfirmation?: (show: boolean) => void;
}

interface CategoryHoverMenuProps {
  categories: Category[];
  categoriesMap: Map<number, Category[]>;
  onSelectCategory: (id: number | null) => void;
  selectedCategory: number | null;
}

interface CategoryColumnProps {
  categories: Category[];
  categoriesMap: Map<number, Category[]>;
  onSelectCategory: (id: number | null) => void;
  onHover: (id: number | null) => void;
  selectedCategory: number | null;
}

interface CategoryItemProps {
  category: Category;
  hasChildren: boolean;
  isSelected: boolean;
  onSelect: () => void;
  onHover: () => void;
}

const ProductEditForm: React.FC<ProductEditFormProps> = ({
  editingProduct,
  setEditingProduct,
  categories,
  onSave,
  onCancel,
  saving,
  showDeleteVariantConfirmation,
  setShowDeleteVariantConfirmation,
  handleDeleteVariant,
  handleAddVariant,
  handleGenerateDescription,
  generatingDescription,
  uploadingImages,
  imageError,
  handleImageUpload,
  handleDeleteImage,
  handleSetPrincipalImage,
  handleReorderImages,
  handleDeleteProduct,
  showDeleteProductConfirmation,
  setShowDeleteProductConfirmation
}) => {
  const [selectedCategory, setSelectedCategory] = useState<number | null>(editingProduct?.categoria_id || null);
  const [showCategoryMenu, setShowCategoryMenu] = useState(false);

  // Compute categoriesMap for the hover menu
  const categoriesMap = useMemo(() => {
    const map = new Map<number, Category[]>();
    
    // Process all categories and group by parent
    categories.forEach(category => {
      const pathSegments = category.path.split('/').filter(Boolean);
      
      if (pathSegments.length > 1) {
        // Find parent category based on path
        const parentPath = '/' + pathSegments.slice(0, -1).join('/');
        const parentCategory = categories.find(c => c.path === parentPath);
        
        if (parentCategory) {
          if (!map.has(parentCategory.id)) {
            map.set(parentCategory.id, []);
          }
          map.get(parentCategory.id)!.push(category);
        }
      } else {
        // Root level categories
        if (!map.has(0)) {
          map.set(0, []);
        }
        map.get(0)!.push(category);
      }
    });
    
    return map;
  }, [categories]);

  if (!editingProduct) return null;

  // Process variant images to ensure proper format
  const processedEditingProduct = useMemo(() => {
    return {
      ...editingProduct,
      variants: editingProduct.variants.map(variant => {
        // Ensure images is always a properly formatted array
        const processedImages = Array.isArray(variant.images) 
          ? variant.images.map(img => ({
              id: img.id,
              variante_id: variant.id || 0,
              producto_variante_id: variant.id || 0,
              url: img.url,
              principal: img.principal || false,
              orden: img.orden || 0
            }))
          : [];
        
        return {
          ...variant,
          images: processedImages
        };
      })
    };
  }, [editingProduct]);

  return (
    <div className="fixed inset-0 bg-brand-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b">
          <h2 className="text-xl font-semibold text-gray-800">
            {processedEditingProduct.id ? 'Editar producto' : 'Nuevo producto'}
          </h2>
        </div>

        <div className="p-6">
          <div className="space-y-6">
            {/* Product name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nombre del producto
              </label>
              <input
                type="text"
                value={processedEditingProduct.nombre}
                onChange={(e) => setEditingProduct({
                  ...processedEditingProduct,
                  nombre: e.target.value
                })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 focus:outline-none"
                placeholder="Nombre del producto"
              />
            </div>

            {/* Category selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Categoría
              </label>
              <div className="relative">
                <div 
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 focus:outline-none flex justify-between items-center cursor-pointer"
                  onClick={() => setShowCategoryMenu(!showCategoryMenu)}
                >
                  <span>
                    {processedEditingProduct.categoria_id ? 
                      categories.find(cat => cat.id === processedEditingProduct.categoria_id)?.nombre || 'Seleccionar categoría' : 
                      'Seleccionar categoría'}
                  </span>
                  <ChevronDown size={16} />
                </div>
                
                {/* Show the CategoryHoverMenu when toggled */}
                {showCategoryMenu && (
                  <div className="absolute z-50 mt-1 w-full">
                    <CategoryHoverMenu
                      categories={categories}
                      categoriesMap={categoriesMap}
                      onSelectCategory={(id) => {
                        setEditingProduct({
                          ...processedEditingProduct,
                          categoria_id: id
                        });
                        setSelectedCategory(id);
                        setShowCategoryMenu(false);
                      }}
                      selectedCategory={selectedCategory}
                    />
                  </div>
                )}
              </div>
            </div>

            {/* URLs and prices */}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  URL producto online
                </label>
                <input
                  type="text"
                  value={processedEditingProduct.url_pagina_web || ''}
                  onChange={(e) => setEditingProduct({
                    ...processedEditingProduct,
                    url_pagina_web: e.target.value
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  placeholder="https://..."
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Costo promedio (ARS)
                </label>
                <input
                  type="number"
                  value={processedEditingProduct.costo_promedio}
                  onChange={(e) => setEditingProduct({
                    ...processedEditingProduct,
                    costo_promedio: parseFloat(e.target.value)
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:outline-none"
                />
                {processedEditingProduct.variants.length > 1 && (
                  <div className="mt-1 flex items-center">
                    <input 
                      type="checkbox" 
                      id="applyCostToVariants"
                      checked={processedEditingProduct.applyCostToVariants}
                      onChange={(e) => setEditingProduct({
                        ...processedEditingProduct,
                        applyCostToVariants: e.target.checked
                      })}
                      className="mr-2"
                    />
                    <label htmlFor="applyCostToVariants" className="text-xs text-gray-600">
                      Aplicar a todas las variantes
                    </label>
                  </div>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Precio local (ARS)
                </label>
                <input
                  type="number"
                  value={processedEditingProduct.precio_local}
                  onChange={(e) => setEditingProduct({
                    ...processedEditingProduct,
                    precio_local: parseFloat(e.target.value)
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:outline-none"
                />
                {processedEditingProduct.variants.length > 1 && (
                  <div className="mt-1 flex items-center">
                    <input 
                      type="checkbox" 
                      id="applyPriceLocalToVariants"
                      checked={processedEditingProduct.applyPriceLocalToVariants}
                      onChange={(e) => setEditingProduct({
                        ...processedEditingProduct,
                        applyPriceLocalToVariants: e.target.checked
                      })}
                      className="mr-2"
                    />
                    <label htmlFor="applyPriceLocalToVariants" className="text-xs text-gray-600">
                      Aplicar a todas las variantes
                    </label>
                  </div>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Precio web (ARS)
                </label>
                <input
                  type="number"
                  value={processedEditingProduct.precio_web}
                  onChange={(e) => setEditingProduct({
                    ...processedEditingProduct,
                    precio_web: parseFloat(e.target.value)
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:outline-none"
                />
                {processedEditingProduct.variants.length > 1 && (
                  <div className="mt-1 flex items-center">
                    <input 
                      type="checkbox" 
                      id="applyPriceWebToVariants"
                      checked={processedEditingProduct.applyPriceWebToVariants}
                      onChange={(e) => setEditingProduct({
                        ...processedEditingProduct,
                        applyPriceWebToVariants: e.target.checked
                      })}
                      className="mr-2"
                    />
                    <label htmlFor="applyPriceWebToVariants" className="text-xs text-gray-600">
                      Aplicar a todas las variantes
                    </label>
                  </div>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Precio ML (ARS)
                </label>
                <input
                  type="number"
                  value={processedEditingProduct.precio_ml}
                  onChange={(e) => setEditingProduct({
                    ...processedEditingProduct,
                    precio_ml: parseFloat(e.target.value)
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:outline-none"
                />
                {processedEditingProduct.variants.length > 1 && (
                  <div className="mt-1 flex items-center">
                    <input 
                      type="checkbox" 
                      id="applyPriceMlToVariants"
                      checked={processedEditingProduct.applyPriceMlToVariants}
                      onChange={(e) => setEditingProduct({
                        ...processedEditingProduct,
                        applyPriceMlToVariants: e.target.checked
                      })}
                      className="mr-2"
                    />
                    <label htmlFor="applyPriceMlToVariants" className="text-xs text-gray-600">
                      Aplicar a todas las variantes
                    </label>
                  </div>
                )}
              </div>
            </div>

            {/* Variants section */}
            <div className="mt-8">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-800">Variantes</h3>
                {processedEditingProduct.id && (
                  <button
                    onClick={() => setShowDeleteProductConfirmation?.(true)}
                    className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md transition-colors shadow-sm font-normal"
                  >
                    Eliminar producto y todas sus variantes
                  </button>
                )}
              </div>
              
              {showDeleteProductConfirmation && (
                <div className="mb-4 p-4 border border-red-200 rounded-lg bg-red-50">
                  <p className="mb-2 text-red-700">¿Estás seguro que deseas eliminar este producto y todas sus variantes? Esta acción no se puede deshacer.</p>
                  <div className="flex justify-end space-x-2">
                    <button
                      onClick={() => setShowDeleteProductConfirmation?.(false)}
                      className="px-3 py-1 text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
                    >
                      Cancelar
                    </button>
                    <button
                      onClick={() => handleDeleteProduct?.()}
                      className="px-3 py-1 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors font-normal"
                    >
                      Eliminar
                    </button>
                  </div>
                </div>
              )}
              
              <div className="space-y-8">
                {processedEditingProduct.variants.map((variant, index) => (
                  <div key={variant.id || index} className="border rounded-lg p-4 bg-gray-50">
                    {showDeleteVariantConfirmation === variant.id && (
                      <div className="mb-4 p-4 border border-red-200 rounded-lg bg-red-50">
                        <p className="mb-2 text-red-700">¿Estás seguro que deseas eliminar esta variante?</p>
                        <div className="flex justify-end space-x-2">
                          <button
                            onClick={() => setShowDeleteVariantConfirmation(null)}
                            className="px-3 py-1 text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
                          >
                            Cancelar
                          </button>
                          <button
                            onClick={() => handleDeleteVariant(variant.id!)}
                            className="px-3 py-1 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
                          >
                            Eliminar
                          </button>
                        </div>
                      </div>
                    )}
                    
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Nombre de la variante
                          </label>
                          <input
                            type="text"
                            value={variant.nombre}
                            onChange={(e) => setEditingProduct({
                              ...processedEditingProduct,
                              variants: processedEditingProduct.variants.map((v, i) =>
                                i === index ? { ...v, nombre: e.target.value } : v
                              )
                            })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:outline-none"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            SKU
                          </label>
                          <input
                            type="text"
                            value={variant.sku || ''}
                            onChange={(e) => setEditingProduct({
                              ...processedEditingProduct,
                              variants: processedEditingProduct.variants.map((v, i) =>
                                i === index ? { ...v, sku: e.target.value } : v
                              )
                            })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:outline-none"
                          />
                        </div>
                      </div>
                      
                      <div>
                        <div className="flex justify-between items-center mb-1">
                          <label className="block text-sm font-medium text-gray-700">
                            Descripción
                          </label>
                          <button
                            onClick={() => handleGenerateDescription('variant', variant.id)}
                            disabled={generatingDescription === 'variant'}
                            className="text-xs text-blue-600 hover:text-blue-700 flex items-center"
                          >
                            {generatingDescription === 'variant' ? (
                              <>
                                <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                Generando...
                              </>
                            ) : (
                              'Generar con IA'
                            )}
                          </button>
                        </div>
                        <textarea
                          value={variant.descripcion || ''}
                          onChange={(e) => setEditingProduct({
                            ...processedEditingProduct,
                            variants: processedEditingProduct.variants.map((v, i) =>
                              i === index ? { ...v, descripcion: e.target.value } : v
                            )
                          })}
                          rows={3}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:outline-none"
                        />
                        {processedEditingProduct.variants.length > 1 && (
                          <div className="mt-1 flex items-center">
                            <input 
                              type="checkbox" 
                              id={`applyDescriptionToVariants-${index}`}
                              checked={processedEditingProduct.applyDescriptionToVariants}
                              onChange={(e) => setEditingProduct({
                                ...processedEditingProduct,
                                applyDescriptionToVariants: e.target.checked
                              })}
                              className="mr-2"
                            />
                            <label htmlFor={`applyDescriptionToVariants-${index}`} className="text-xs text-gray-600">
                              Aplicar a todas las variantes
                            </label>
                          </div>
                        )}
                      </div>
                      
                      {/* Attributes section */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Atributos
                        </label>
                        <div className="space-y-3">
                          {variant.atributos && Object.entries(variant.atributos).map(([key, value]) => (
                            <div key={key} className="flex items-center space-x-2">
                              <input
                                type="text"
                                value={key}
                                onChange={(e) => {
                                  if (!variant.atributos) return;
                                  const newKey = e.target.value;
                                  const newAtributos = { ...variant.atributos };
                                  const currentValue = newAtributos[key];
                                  delete newAtributos[key];
                                  newAtributos[newKey] = currentValue;
                                  
                                  setEditingProduct({
                                    ...processedEditingProduct,
                                    variants: processedEditingProduct.variants.map((v, i) =>
                                      i === index ? { ...v, atributos: newAtributos } : v
                                    )
                                  });
                                }}
                                placeholder="Atributo"
                                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:outline-none"
                              />
                              <input
                                type="text"
                                value={value}
                                onChange={(e) => {
                                  if (!variant.atributos) return;
                                  setEditingProduct({
                                    ...processedEditingProduct,
                                    variants: processedEditingProduct.variants.map((v, i) =>
                                      i === index ? {
                                        ...v,
                                        atributos: { ...v.atributos, [key]: e.target.value }
                                      } : v
                                    )
                                  });
                                }}
                                placeholder="Valor"
                                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:outline-none"
                              />
                              <button
                                type="button"
                                onClick={() => {
                                  if (!variant.atributos) return;
                                  const newAtributos = { ...variant.atributos };
                                  delete newAtributos[key];
                                  setEditingProduct({
                                    ...processedEditingProduct,
                                    variants: processedEditingProduct.variants.map((v, i) =>
                                      i === index ? { ...v, atributos: newAtributos } : v
                                    )
                                  });
                                }}
                                className="px-3 py-2 text-red-600 hover:bg-red-50 rounded-md transition-colors"
                              >
                                <X className="w-5 h-5" />
                              </button>
                            </div>
                          ))}
                          <button
                            type="button"
                            onClick={() => {
                              setEditingProduct({
                                ...processedEditingProduct,
                                variants: processedEditingProduct.variants.map((v, i) =>
                                  i === index ? {
                                    ...v,
                                    atributos: {
                                      ...v.atributos,
                                      '': ''
                                    }
                                  } : v
                                )
                              });
                            }}
                            className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                          >
                            + Agregar atributo
                          </button>
                        </div>
                      </div>

                      {!processedEditingProduct.applyCostToVariants && (
                        <div className="grid grid-cols-1 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Costo promedio (ARS)
                            </label>
                            <input
                              type="number"
                              value={variant.costo_promedio}
                              onChange={(e) => setEditingProduct({
                                ...processedEditingProduct,
                                variants: processedEditingProduct.variants.map((v, i) =>
                                  i === index ? { ...v, costo_promedio: parseFloat(e.target.value) } : v
                                )
                              })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:outline-none"
                            />
                          </div>
                        </div>
                      )}

                      {!processedEditingProduct.applyPriceLocalToVariants && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Precio local (ARS)
                          </label>
                          <input
                            type="number"
                            value={variant.precio_local}
                            onChange={(e) => setEditingProduct({
                              ...processedEditingProduct,
                              variants: processedEditingProduct.variants.map((v, i) =>
                                i === index ? { ...v, precio_local: parseFloat(e.target.value) } : v
                              )
                            })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:outline-none"
                          />
                        </div>
                      )}

                      {!processedEditingProduct.applyPriceWebToVariants && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Precio web (ARS)
                          </label>
                          <input
                            type="number"
                            value={variant.precio_web}
                            onChange={(e) => setEditingProduct({
                              ...processedEditingProduct,
                              variants: processedEditingProduct.variants.map((v, i) =>
                                i === index ? { ...v, precio_web: parseFloat(e.target.value) } : v
                              )
                            })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:outline-none"
                          />
                        </div>
                      )}

                      {!processedEditingProduct.applyPriceMlToVariants && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Precio ML (ARS)
                          </label>
                          <input
                            type="number"
                            value={variant.precio_ml}
                            onChange={(e) => setEditingProduct({
                              ...processedEditingProduct,
                              variants: processedEditingProduct.variants.map((v, i) =>
                                i === index ? { ...v, precio_ml: parseFloat(e.target.value) } : v
                              )
                            })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:outline-none"
                          />
                        </div>
                      )}

                      {processedEditingProduct.variants.length > 1 && (
                        <div className="flex justify-end">
                          <button
                            onClick={() => setShowDeleteVariantConfirmation(variant.id!)}
                            className="px-4 py-2 text-red-600 hover:bg-red-50 rounded-md transition-colors"
                          >
                            Eliminar variante
                          </button>
                        </div>
                      )}
                      
                      {/* Image Management Section */}
                      <div className="mt-4 border border-gray-200 rounded-md p-4 bg-gray-50">
                        <h4 className="text-sm font-medium text-gray-700 mb-3">Gestión de imágenes</h4>
                        <VariantImageManager
                          variantId={variant.id!}
                          images={processedEditingProduct.variants.find(v => v.id === variant.id)?.images || []}
                          uploadingImages={uploadingImages}
                          imageError={imageError}
                          onUpload={handleImageUpload}
                          onDelete={handleDeleteImage}
                          onSetPrincipal={handleSetPrincipalImage}
                          onReorder={handleReorderImages}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* "Agregar variante" button - always visible */}
              <div className="flex justify-end mt-4">
                <button
                  onClick={handleAddVariant}
                  className="px-4 py-2 text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                >
                  + Agregar variante
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 p-6 border-t bg-gray-50 rounded-b-2xl">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
          >
            Cancelar
          </button>
          <button
            onClick={onSave}
            disabled={saving}
            className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors disabled:opacity-50 flex items-center gap-2 shadow-sm"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Guardando...
              </>
            ) : (
              'Guardar cambios'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

// CategoryHoverMenu component for a hover-based multi-column dropdown
const CategoryHoverMenu: React.FC<CategoryHoverMenuProps> = ({
  categories,
  categoriesMap,
  onSelectCategory,
  selectedCategory
}) => {
  // State to track which category columns are currently visible
  const [hoveredCategories, setHoveredCategories] = useState<number[]>([]);
  
  // Function to handle hovering over a category
  const handleCategoryHover = (categoryId: number | null, columnIndex: number) => {
    if (categoryId === null) {
      // If hovering away, remove all columns after the current index
      setHoveredCategories(prev => prev.slice(0, columnIndex));
    } else {
      // If hovering on a category, update columns after this index
      setHoveredCategories(prev => {
        const newHovered = [...prev.slice(0, columnIndex)];
        newHovered.push(categoryId);
        return newHovered;
      });
    }
  };
  
  // Root categories - those without parents
  const rootCats = useMemo(() => {
    return categories.filter(category => {
      const segments = category.path.split('/').filter(Boolean);
      return segments.length === 1;
    });
  }, [categories]);
  
  return (
    <div className="relative" style={{ height: '300px' }}>
      {/* First column with root categories */}
      <div className="absolute left-0 top-0 w-64 bg-white rounded-md shadow-lg border border-gray-200 z-30 overflow-hidden">
        <div className="p-1">
          <button
            onClick={() => onSelectCategory(null)}
            className="w-full px-4 py-2 text-left hover:bg-gray-50 text-gray-800 rounded-md"
          >
            Todas las categorías
          </button>
          
          <CategoryColumn
            categories={rootCats}
            categoriesMap={categoriesMap}
            onSelectCategory={onSelectCategory}
            onHover={(id) => handleCategoryHover(id, 0)}
            selectedCategory={selectedCategory}
          />
        </div>
      </div>
      
      {/* Render additional columns for each hovered category */}
      {hoveredCategories.map((categoryId, index) => {
        // Get the child categories for this hovered category
        const childCategories = categoriesMap.get(categoryId) || [];
        
        if (childCategories.length === 0) return null;
        
        return (
          <div 
            key={categoryId}
            className="absolute top-0 w-64 bg-white rounded-md shadow-lg border border-gray-200 z-30 overflow-hidden"
            style={{ left: `${(index + 1) * 16}rem` }}
          >
            <div className="p-1">
              <CategoryColumn
                categories={childCategories}
                categoriesMap={categoriesMap}
                onSelectCategory={onSelectCategory}
                onHover={(id) => handleCategoryHover(id, index + 1)}
                selectedCategory={selectedCategory}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
};

// Column of category items
const CategoryColumn: React.FC<CategoryColumnProps> = ({
  categories,
  categoriesMap,
  onSelectCategory,
  onHover,
  selectedCategory
}) => {
  return (
    <div className="max-h-80 overflow-y-auto">
      {categories.map(category => (
        <CategoryItem
          key={category.id}
          category={category}
          hasChildren={categoriesMap.has(category.id)}
          isSelected={selectedCategory === category.id}
          onSelect={() => onSelectCategory(category.id)}
          onHover={() => onHover(category.id)}
        />
      ))}
    </div>
  );
};

// Individual category item
const CategoryItem: React.FC<CategoryItemProps> = ({
  category,
  hasChildren,
  isSelected,
  onSelect,
  onHover
}) => {
  return (
    <div
      className={`flex items-center px-4 py-2 hover:bg-gray-100 transition-colors ${isSelected ? 'bg-blue-100 text-blue-800' : 'text-gray-800'}`}
      onMouseEnter={onHover}
    >
      <div 
        className="flex-1 cursor-pointer"
        onClick={onSelect}
      >
        {category.nombre}
      </div>
      {hasChildren && (
        <div className="flex items-center justify-center w-6">
          <ChevronRight className="w-4 h-4 text-gray-500" />
        </div>
      )}
    </div>
  );
};

export default ProductEditForm;
